import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Typography from '@mui/material/Typography'
import DialogContent from '@mui/material/DialogContent/DialogContent'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { AddKnockStatusForm } from './AddKnockStatusForm'
import { useHandlers, useHistoryData } from './hooks'
import { EntityLocationDTO } from '@user/store/map/typings'
import { KnockStatusLocationBaseInput } from '@user/store/knockStatus'

type Props = {
  open: boolean
  onClose: (hasExistingHistory?: boolean) => void
  /** SH-1781: called with the saved location's id so callers can scope a reset to it. */
  onRefresh?: () => void
  onDeleteSuccess?: () => void
  address?: EntityLocationDTO | null
  onSaveNewKnockStatusLocation?: (
    newKnockLocation: KnockStatusLocationBaseInput,
  ) => void
}

export function KnockStatusModal({
  open,
  onClose,
  onRefresh,
  address = null,
  onSaveNewKnockStatusLocation,
}: Props) {
  const { reload, hasHistory, activeKnockStatusName } = useHistoryData(address)
  const { onModalClose } = useHandlers({
    onClose,
    reload,
    hasExistingHistory: hasHistory,
    currentKnockStatusName: activeKnockStatusName,
    onRefresh,
    address: address,
    onSaveNewKnockStatusLocation,
  })
  const theme = useTheme()
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const isSmallHeightMobile = useMediaQuery(
    '(max-height: 500px) and (pointer: coarse)',
  )
  const isFullScreen = isSmallWidth || isSmallHeightMobile

  return (
    <Dialog
      open={open}
      onClose={onModalClose}
      fullScreen={isFullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={
        isFullScreen
          ? undefined
          : {
              sx: {
                width: {
                  xs: '95vw',
                  sm: '501px',
                },
                maxWidth: {
                  xs: '95vw',
                  sm: '501px',
                },
                maxHeight: '90vh',
                overflowY: 'auto',
              },
            }
      }
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 0,
          height: '72px',
          pl: '24px',
          pr: '15px',
          pt: '24px',
          pb: '16px',
        }}
      >
        <Typography
          variant={'h6'}
          component="span"
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
        >
          Knock Status
        </Typography>

        <IconButton
          onClick={() => onClose()}
          size={'medium'}
          sx={{
            color: (theme) => theme.palette.action.active,
            cursor: 'pointer',
            borderRadius: '100px',
          }}
          data-testid="action-modal-close-btn"
          data-test-id="knockStatusModalCloseBtn"
        >
          <CloseIcon fontSize={'medium'} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          px: 2.4,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AddKnockStatusForm
          onClose={onClose}
          reload={reload}
          hasExistingHistory={hasHistory}
          currentKnockStatusName={activeKnockStatusName}
          onRefresh={onRefresh}
          address={address}
          onSaveNewKnockStatusLocation={onSaveNewKnockStatusLocation}
        />
      </DialogContent>
    </Dialog>
  )
}
